import type { SerializedUser } from '@enmon/commons/lib/enmon/users/user';
import { createAuthClient } from 'better-auth/react';
import { customSessionClient, twoFactorClient } from 'better-auth/client/plugins';
import { config } from '@/config';
import { sessionCustomerClient, type SessionCustomerActions } from './auth-client-session-plugin';

const resolveBaseURL = (url: string) => {
  try {
    return new URL(url).toString();
  } catch {
    return new URL(url, window.location.origin).toString();
  }
};

const baseAuthClient = createAuthClient({
  baseURL: resolveBaseURL(config.apiBaseUrl),
  basePath: '/auth/v2',
  fetchOptions: {
    credentials: 'include', // Include cookies in requests
  },
  plugins: [customSessionClient(), sessionCustomerClient(), twoFactorClient()],
});

type SignInEmailResponse = {
  data?:
    | {
        user: SerializedUser;
        token: string;
        redirect: boolean;
      }
    | { twoFactorRedirect: boolean }
    | null;
  error?: unknown;
};

type SessionUser = {
  id: string;
  email: string;
  name?: string;
  [key: string]: unknown;
};

type GetSessionResponse = {
  data?: {
    user: SessionUser;
    session: {
      activeCustomerId?: string;
      customerRequiresTwoFactor?: boolean;
      twoFactorEnabled?: boolean;
      [key: string]: unknown;
    };
  } | null;
  error?: unknown;
};

type AuthClientWithOverrides = Omit<typeof baseAuthClient, 'signIn' | 'getSession'> &
  SessionCustomerActions & {
    signIn: Omit<(typeof baseAuthClient)['signIn'], 'email'> & {
      email: (credentials: { email: string; password: string }) => Promise<SignInEmailResponse>;
    };
    getSession: () => Promise<GetSessionResponse>;
  };

export const authClient = baseAuthClient as AuthClientWithOverrides;
